/**
 * CaribbeanLeaf Application Shell
 *
 * Main layout with sidebar navigation for the cannabis dispensary POS.
 * Features:
 * - Collapsible sidebar for desktop
 * - Mobile-responsive hamburger menu
 * - Tenant name display
 * - User info and logout
 *
 * Uses Tropical Brutalism design system.
 */

import { type ReactNode, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Package,
  FileText,
  Settings,
  LogOut,
  Leaf,
  Menu,
  X,
  ClipboardList,
  MapPin,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

// ─────────────────────────────────────────────────────────────────────────────
// Navigation Items
// ─────────────────────────────────────────────────────────────────────────────

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/pos', icon: ShoppingCart, label: 'Point of Sale' },
  { to: '/staff/orders', icon: ClipboardList, label: 'Orders' },
  { to: '/patients', icon: Users, label: 'Patients' },
  { to: '/inventory', icon: Package, label: 'Inventory' },
  { to: '/compliance', icon: FileText, label: 'Compliance' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const adminNavItems = [
  { to: '/admin/users', icon: Users, label: 'User Management' },
  { to: '/admin/locations', icon: MapPin, label: 'Locations' },
  { to: '/admin/sacramental', icon: Leaf, label: 'Sacramental Queue' },
  { to: '/admin/settings', icon: Shield, label: 'Admin Settings' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) closeSidebar();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  // User initials for avatar
  const userInitials =
    user?.first_name?.[0]?.toUpperCase() ??
    user?.email?.[0]?.toUpperCase() ??
    '?';

  // User display name
  const displayName = user?.full_name || user?.email || '';

  // User role
  const userRole = user?.role?.replace('_', ' ') ?? '';

  return (
    <div className="min-h-screen bg-surface-primary">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-cannabis focus:text-white focus:rounded">
        Skip to content
      </a>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-surface-secondary border-b border-brutal-border z-50 flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-text-primary hover:bg-surface-tertiary"
          aria-expanded={sidebarOpen}
          aria-label="Main menu"
        >
          {sidebarOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </Button>
        <div className="flex items-center gap-2 ml-3">
          <Leaf className="h-5 w-5 text-cannabis" />
          <span className="font-semibold text-text-primary">CaribbeanLeaf</span>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full w-64 bg-surface-secondary border-r border-brutal-border z-40',
          'transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Sidebar Header */}
        <div className="h-14 flex items-center gap-3 px-4 border-b border-brutal-border">
          <div className="p-1.5 bg-cannabis/20 rounded border border-cannabis/30">
            <Leaf className="h-5 w-5 text-cannabis" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-text-primary text-sm">
              CaribbeanLeaf
            </span>
            <p className="text-xs text-text-tertiary truncate">
              {tenant?.business_name || 'Cannabis POS'}
            </p>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="p-3 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeSidebar}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium',
                  'transition-colors duration-150',
                  isActive
                    ? 'bg-cannabis/20 text-cannabis border-l-2 border-cannabis'
                    : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                )
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}

          {/* Admin Section */}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <>
              <div className="pt-3 pb-1 px-3">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Admin
                </span>
              </div>
              {adminNavItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium',
                      'transition-colors duration-150',
                      isActive
                        ? 'bg-cannabis/20 text-cannabis border-l-2 border-cannabis'
                        : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                    )
                  }
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-brutal-border bg-surface-secondary">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-cannabis/20 border border-cannabis/30 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-cannabis">
                {userInitials}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {displayName}
              </p>
              {userRole && (
                <p className="text-xs text-text-tertiary truncate capitalize">
                  {userRole}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start border-brutal-border text-text-secondary hover:text-coral hover:border-coral/50"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div
            className="fixed inset-0 bg-black/50"
            onClick={closeSidebar}
          />
        </div>
      )}

      {/* Main Content */}
      <main id="main-content" className="lg:pl-64 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-6">{children}</div>
      </main>
    </div>
  );
}
